import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useWallet } from './hooks/useWallet';
import { useVault } from './hooks/useVault';
import VaultOverview from './components/VaultOverview';
import VaultActionsV2 from './components/VaultActionsV2';
import NoticeBell from './components/NoticeBell';
import LandingPage from './components/LandingPage';
import ContractsPage from './components/ContractsPage';
import AuctionsPage from './components/AuctionsPage';
import SystemPage from './components/SystemPage';
import FaqPage from './components/FaqPage';
import LandingTabs from './components/LandingTabs';
import VaultModeTabs from './components/VaultModeTabs';
import EasyVault from './components/EasyVault';
import { 
  AppContainer, 
  Header, 
  HeaderContent, 
  Logo, 
  WalletButton, 
  NetworkBadge,
  MainContent,
  Alert 
} from './components/StyledComponents';
import { truncateAddress } from './utils/formatters';
import { CONTRACTS, DEXSCREENER, APP_VERSION, AUCTIONS_QA_ALLOWLIST } from './utils/contracts';

const App = () => {
  const { 
    account, 
    signer, 
    isConnecting, 
    isCorrectNetwork, 
    connectWallet, 
    disconnect,
    switchToPulseChain
  } = useWallet();  const {
    vaultData,
    systemData,
    balances,
    contracts,
    refresh
  } = useVault(signer, account);

  // Top-level view, synced with the URL hash so every page is deep-linkable:
  //   #/overview  #/vault  #/auctions  #/system  #/contracts
  // (Hash URLs because GitHub Pages can't rewrite real paths for an SPA —
  // a direct visit to /auctions would 404; /#/auctions loads perfectly.)
  const VIEWS = ['overview', 'vault', 'auctions', 'system', 'faq', 'contracts'];
  const viewFromHash = () => {
    const h = window.location.hash.replace(/^#\/?/, '').split('?')[0].toLowerCase();
    return VIEWS.includes(h) ? h : null;
  };
  const [view, setViewState] = useState(() => viewFromHash() || 'overview');
  const setView = (v) => {
    setViewState(v);
    const target = '#/' + v;
    if (window.location.hash !== target) window.history.pushState(null, '', target);
  };
  // back/forward buttons + hand-edited hashes
  useEffect(() => {
    const onNav = () => { const v = viewFromHash(); if (v) setViewState(v); };
    window.addEventListener('hashchange', onNav);
    window.addEventListener('popstate', onNav);
    return () => { window.removeEventListener('hashchange', onNav); window.removeEventListener('popstate', onNav); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // On connect/disconnect: respect an explicit deep link; otherwise default to
  // the vault when connected, overview when not. A #/vault link without a
  // wallet falls back to overview (the vault view needs an account).
  useEffect(() => {
    const h = viewFromHash();
    if (h && !(h === 'vault' && !account)) { setViewState(h); return; }
    setView(account ? 'vault' : 'overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  // QA gate: Auctions tab visible only to allowlisted addresses until the
  // liquidation/auction stack is vetted. Empty allowlist = public (go-live).
  const auctionsAllowed = AUCTIONS_QA_ALLOWLIST.length === 0 ||
    (!!account && AUCTIONS_QA_ALLOWLIST.includes(account.toLowerCase()));

  // Vault mode tab: sticky per address; first-time default is Easy unless the
  // user already has a Classic (PLS-A) position, which is what they came for.
  const [vaultMode, setVaultMode] = useState('easy');

  // Classic (native-PLS) vaults are legacy: only wallets that actually hold
  // one see the Classic tab. Everyone else gets the single vault view.
  const hasClassicVault = parseFloat(vaultData.collateral) > 0 || parseFloat(vaultData.debt) > 0;
  const effectiveVaultMode = hasClassicVault ? vaultMode : 'easy';
  useEffect(() => {
    if (!account) return;
    try {
      const saved = localStorage.getItem(`jdai-vault-mode-${account}`);
      if (saved === 'easy' || saved === 'classic') { setVaultMode(saved); return; }
      if (parseFloat(vaultData.collateral) > 0 || parseFloat(vaultData.debt) > 0) setVaultMode('classic');
    } catch (e) { /* default stands */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, vaultData.isLoading]);
  const changeVaultMode = (m) => {
    setVaultMode(m);
    try { localStorage.setItem(`jdai-vault-mode-${account}`, m); } catch (e) { /* non-fatal */ }
  };

  const handleConnect = async () => {
    try {
      await connectWallet();
      toast.success('Wallet connected successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to connect wallet');
    }
  };

  const handleNetworkSwitch = async () => {
    try {
      await switchToPulseChain();
      toast.success('Switched to PulseChain!');
    } catch (error) {
      toast.error('Failed to switch network');
    }
  };
  const addJDAIToWallet = async () => {
    if (!window.ethereum) {
      toast.error('No wallet detected');
      return;
    }

    try {
      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: CONTRACTS.JDAI,
            symbol: 'JDAI',
            decimals: 18,
          },
        },
      });
      toast.success('JDAI token added to wallet!');
    } catch (error) {
      toast.error('Failed to add token to wallet');
    }
  };

  const openDexScreener = (tokenAddress) => {
    window.open(`https://dexscreener.com/pulsechain/${tokenAddress}`, '_blank');
  };

  const openTelegram = () => {
    window.open('https://t.me/USCG_Vet_Chat', '_blank');
  };
  const openTwitter = () => {
    window.open('https://x.com/uscgvet5555', '_blank');
  };

  const openSourceCode = (contractAddress) => {
    window.open(`https://repo.sourcify.dev/369/${contractAddress}`, '_blank');
  };

  return (
    <AppContainer>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid #333'
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
        <Header>
        <HeaderContent>
          <Logo>
            <div>
              <h1 onClick={addJDAIToWallet} style={{ cursor: 'pointer' }} title="Click to add JDAI to wallet">
                JDAI
              </h1>
              <div className="subtitle">Gold-Pegged Unstablecoin on PulseChain</div>              {/* Contract Addresses */}
              <div style={{ 
                fontSize: '0.7rem', 
                color: '#666', 
                marginTop: '0.5rem',
                fontFamily: 'monospace'
              }}>
                <div style={{ marginBottom: '0.2rem' }}>
                  <span 
                    onClick={() => openSourceCode(CONTRACTS.JDAI)}
                    style={{
                      cursor: 'pointer',
                      color: '#4ade80',
                      textDecoration: 'underline',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#22c55e'}
                    onMouseLeave={(e) => e.target.style.color = '#4ade80'}
                    title="View JDAI source code on Sourcify"
                  >
                    ✅ JDAI: {CONTRACTS.JDAI}
                  </span>
                </div>
                <div>
                  <span 
                    onClick={() => openSourceCode(CONTRACTS.TKR)}
                    style={{
                      cursor: 'pointer',
                      color: '#4ade80',
                      textDecoration: 'underline',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#22c55e'}
                    onMouseLeave={(e) => e.target.style.color = '#4ade80'}
                    title="View TKR source code on Sourcify"
                  >
                    ✅ TKR: {CONTRACTS.TKR}
                  </span>
                </div>
              </div>
            </div>
          </Logo>
          
          {/* Middle section - DexScreener and Social Links */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>            {/* DexScreener Buttons */}
            <button
              onClick={() => openDexScreener(DEXSCREENER.JDAI)}
              style={{
                background: 'linear-gradient(45deg, #ffd700, #f59e0b)',
                color: '#000',
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                lineHeight: '1.2'
              }}
              title="View JDAI on DexScreener"
            >
              <div>📊 JDAI</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>DexScreener</div>
            </button>
            
            <button
              onClick={() => openDexScreener(DEXSCREENER.TKR)}
              style={{
                background: 'linear-gradient(45deg, #10b981, #059669)',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                lineHeight: '1.2'
              }}
              title="View TKR on DexScreener"
            >
              <div>📊 TKR</div>
              <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>DexScreener</div>
            </button>
            
            {/* Social Links */}
            <button
              onClick={openTelegram}
              style={{
                background: '#0088cc',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              title="Join Telegram Chat"
            >
              💬 Telegram
            </button>
            
            <button
              onClick={openTwitter}
              style={{
                background: '#1da1f2',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '0.4rem 0.8rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              title="Follow on X (Twitter)"
            >
              🐦 X
            </button>
          </div>
            {/* Right section - Network and Wallet */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {account && (
              <NetworkBadge $correct={isCorrectNetwork}>
                {isCorrectNetwork ? 'PulseChain' : 'Wrong Network'}
              </NetworkBadge>
            )}
            {/* Wallet-connect notices (recoverable balances, liquidation leftovers)
                live under the bell instead of eating page space as banners. */}
            <NoticeBell signer={signer} account={account} />
            <WalletButton
              $connected={!!account}
              onClick={account ? disconnect : handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? 'Connecting...' : 
               account ? truncateAddress(account) : 'Connect Wallet'}
            </WalletButton>
          </div>
        </HeaderContent>
      </Header>

      <MainContent>
        {/* Top nav — Overview/Contracts always available, My Vault when connected. */}
        <LandingTabs view={view} onChange={setView} connected={!!account} showAuctions={auctionsAllowed} />

        {view === 'overview' && (
          <LandingPage
            onConnect={handleConnect}
            isConnecting={isConnecting}
            connected={!!account}
            onEnterVault={() => setView('vault')}
          />
        )}

        {view === 'contracts' && <ContractsPage />}

        {view === 'auctions' && auctionsAllowed && <AuctionsPage signer={signer} account={account} />}

        {view === 'system' && <SystemPage />}

        {view === 'faq' && <FaqPage />}

        {view === 'vault' && (
          account && !isCorrectNetwork ? (
            <div style={{ gridColumn: '1 / -1' }}>
              <Alert type="warning">
                <div className="title">Wrong Network</div>
                <div className="message">
                  Please switch to PulseChain to use this application.
                </div>
              </Alert>
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <WalletButton onClick={handleNetworkSwitch}>
                  Switch to PulseChain
                </WalletButton>
              </div>
            </div>
          ) : account ? (
            <>
              <VaultModeTabs mode={effectiveVaultMode} onChange={changeVaultMode} showClassic={hasClassicVault} />
              {effectiveVaultMode === 'easy' ? (
                <EasyVault
                  signer={signer}
                  account={account}
                  balances={balances}
                  systemData={systemData}
                  onRefresh={refresh}
                />
              ) : (
                <>
                  <VaultOverview
                    vaultData={vaultData}
                    systemData={systemData}
                    onRefresh={refresh}
                    isLoading={vaultData.isLoading || systemData.isLoading}
                  />                <VaultActionsV2
                    vaultData={vaultData}
                    systemData={systemData}
                    balances={balances}
                    contracts={contracts}
                    isLoading={balances.isLoading}
                    userAddress={account}
                    onRefresh={refresh}
                  />
                </>
              )}
            </>
          ) : null
        )}
      </MainContent>

      {/* Footer */}
      <footer style={{ 
        textAlign: 'center', 
        padding: '2rem', 
        color: '#666',
        borderTop: '1px solid #333'
      }}>        <p>
          JDAI Unstablecoin - Powered by PulseChain | 
          <span style={{ marginLeft: '1rem' }}>
            1 JDAI = 1/1000 oz Gold USD Value
          </span>
        </p>
        <p style={{ fontSize: '0.7rem', color: '#555', marginTop: '0.5rem', fontFamily: 'monospace' }}>
          v{APP_VERSION}
        </p>
      </footer>
    </AppContainer>
  );
};

export default App;
